/* eslint-disable no-fallthrough */

import React, { useState, useMemo, useEffect } from 'react';
import { css } from '@emotion/react';
import { useBusinessTimezone, useClientUser } from '@/hooks';
import { SecondaryButton } from '@/styles/blocks';
import { Grid, RadioGroup, Radio, FormControlLabel } from '@material-ui/core';

import {
    ResponsiveSidebarDialog,
    StickyModalActions,
    ModalContent,
    ModalTitle,
    PrimaryButton,
    WarningButton,
} from '@/styles/blocks';

import RadioLabel from './RadioLabel';

import { DELETE_TYPE_TITLES, UPDATE_TYPES, UPDATE_TYPE_TITLES, STEPS } from '../../constants';
import {
    capacityCeilingsValid,
    ceilingDefaults,
    ceilingFieldErrors,
    isDuplicateNameError,
    setupFieldErrors,
    setupHasError,
} from './validation';
import { emptySchedule, scheduleError, scheduleFromResource } from './schedule';
import {
    buildCreateWrites,
    buildDeleteWrites,
    buildUpdateWrites,
    hasSchedule,
    needsScope,
    onlyOverwrite,
} from './resourceWrites';

import { ModalHeader } from './blocks';
import { buttonLabel } from '../../calendarBlocks';
import SetupTab from './SetupTab';

// Module-scoped so the "new resource" path keeps a stable identity — a fresh {} each render would
// re-fire the effects below.
const EMPTY_RESOURCE = {};

const DELETE_TYPES = [UPDATE_TYPES.SPLIT, UPDATE_TYPES.OVERWRITE, UPDATE_TYPES.DELETE_RESOURCE];

const SCOPE_TYPES = [UPDATE_TYPES.SPLIT, UPDATE_TYPES.OVERWRITE];

const EditResourceModal = ({
    resource = EMPTY_RESOURCE,
    // Today's business day — its key and bounds. The dialog opens from the Fleet grid, never from a
    // day, so this is the "as of" every schedule read and write is measured from.
    day,
    subregions,
    subregionsOrdered,
    isLoading,
    callbacks,
}) => {
    const { shipping_partners, resources: clientResources } = useClientUser();
    const tz = useBusinessTimezone();
    const [updates, setUpdates] = useState({});
    const [shippingPartners, setShippingPartners] = useState([]);
    const [step, setStep] = useState(STEPS.RESOURCE);
    const [updateType, setUpdateType] = useState(UPDATE_TYPES.SPLIT);
    const [regions, setRegions] = useState([]);
    const [schedule, setSchedule] = useState(null);
    // Names the database refused as duplicates that the loaded list did not know about.
    const [refusedNames, setRefusedNames] = useState([]);

    const reset = () => {
        setUpdates({});
        setRegions([]);
        setUpdateType(UPDATE_TYPES.SPLIT);
        setStep(STEPS.RESOURCE);
        setShippingPartners([]);
        setSchedule(null);
        setRefusedNames([]);
    };

    const closeModal = () => {
        reset();
        callbacks.onClose();
    };

    const selectedResource = resource;

    const today = useMemo(() => (day?.start && day?.key ? { start: day.start, key: day.key } : null), [day]);

    useEffect(() => {
        setShippingPartners(
            (selectedResource?.shipping_partners || [])
                .filter((mapping) => mapping.partner_client_id !== null)
                .map((mapping) => mapping.partner_client.client_id)
        );
    }, [selectedResource]);

    useEffect(() => {
        setRegions(
            (selectedResource.subregions || []).map((subregion) => {
                const idx = subregionsOrdered.findIndex((resource) => resource.subregion_id === subregion.subregion_id);
                return [subregion.subregion_id, idx];
            })
        );
    }, [selectedResource, subregionsOrdered]);

    // The schedule as saved, which the form opens on and every save is compared against.
    const saved = useMemo(
        () => (today ? scheduleFromResource(selectedResource, today) : null),
        [selectedResource, today]
    );

    // `schedule` holds edits only. Until the first one the form reads the saved schedule directly, so
    // nothing has to copy it in — a copy re-run whenever `saved` changed identity wiped whatever the
    // operator had changed. A different resource starts clean.
    useEffect(() => {
        setSchedule(null);
    }, [selectedResource]);

    const current = schedule || saved || emptySchedule(today?.key);

    // Defaults under the edits, so a required ceiling that was never set opens filled in rather than
    // blocking Save, and clearing it still reads as cleared.
    const updated = useMemo(() => {
        return {
            ...selectedResource,
            ...ceilingDefaults(selectedResource),
            ...updates,
        };
    }, [updates, selectedResource]);

    // "New" is a resource with no row yet — not a dialog with no rule, which is also how a resource
    // that does not run today opens.
    const isNewResource = !selectedResource?.resource_id;

    // Anything still scheduling a day, live or yet to start. Without one there is nothing for
    // SPLIT or OVERWRITE to end, so Delete offers the whole resource alone.
    const deleteTypes = today && hasSchedule(selectedResource, today) ? DELETE_TYPES : [UPDATE_TYPES.DELETE_RESOURCE];

    // A start date moved into the past rewrites days already run, which only the whole-schedule
    // option does.
    const scopeTypes =
        saved && onlyOverwrite({ schedule: current, saved, today }) ? [UPDATE_TYPES.OVERWRITE] : SCOPE_TYPES;

    const currentError = today ? scheduleError(current, { isNew: isNewResource, todayKey: today.key }) : false;

    // What Save writes, from one of three builders. The Delete step's and an edit's use the saved
    // row and the chosen scope; a new resource has neither.
    const payload = useMemo(() => {
        if (!today) return null;

        if (step === STEPS.DELETE) {
            return buildDeleteWrites({ resource: selectedResource, updateType, today });
        }

        const shared = { form: updated, schedule: current, regions, subregions, shippingPartners, tz };

        return isNewResource
            ? buildCreateWrites(shared)
            : buildUpdateWrites({ ...shared, resource: selectedResource, saved, updateType, today });
    }, [
        today,
        step,
        selectedResource,
        updateType,
        updated,
        current,
        regions,
        subregions,
        shippingPartners,
        tz,
        isNewResource,
        saved,
    ]);

    const modalTitle = selectedResource?.resource_id ? selectedResource.name : 'New Resource';

    // Per field, not just per form. One surface now, so there is no tab left to flag, and a
    // greyed-out Save with nothing marked reads as an unexplained block.
    const takenNames = useMemo(
        () => [
            ...(clientResources || [])
                .filter((other) => other?.resource_id !== selectedResource?.resource_id)
                .map((other) => other?.name),
            ...refusedNames,
        ],
        [clientResources, selectedResource, refusedNames]
    );

    const setupErrors = useMemo(
        () => setupFieldErrors({ updated, scheduleError: currentError, takenNames }),
        [updated, currentError, takenNames]
    );

    const setupComplete = !setupHasError(setupErrors);

    // Blocked rather than clamped on change, so the operator sees their value rejected instead of
    // silently rewritten.
    const capacityValid = useMemo(() => capacityCeilingsValid(updated), [updated]);
    const ceilingErrors = useMemo(() => ceilingFieldErrors(updated), [updated]);

    const isValid = useMemo(() => {
        return (
            (step === STEPS.RESOURCE && setupComplete && capacityValid) ||
            ([STEPS.UPDATE, STEPS.DELETE].includes(step) && !!updateType)
        );
    }, [setupComplete, capacityValid, step, updateType]);

    let content = null;
    switch (step) {
        case STEPS.RESOURCE: {
            content = (
                <SetupTab
                    updated={updated}
                    setUpdates={setUpdates}
                    setupErrors={setupErrors}
                    ceilingErrors={ceilingErrors}
                    schedule={current}
                    // The first edit starts from the saved schedule the form is showing.
                    setSchedule={(update) => setSchedule((prev) => update(prev || current))}
                    scheduleError={currentError}
                    isNew={isNewResource}
                    todayKey={today?.key}
                    regions={regions}
                    setRegions={setRegions}
                    subregions={subregions}
                    subregionsOrdered={subregionsOrdered}
                    shippingPartners={shippingPartners}
                    setShippingPartners={setShippingPartners}
                    shipping_partners={shipping_partners}
                />
            );
            break;
        }
        case STEPS.UPDATE: {
            content = (
                <Grid direction="row" container>
                    <RadioGroup
                        value={updateType || ''}
                        onChange={(e) => {
                            setUpdateType(e.target.value);
                        }}
                    >
                        {scopeTypes.map((type) => (
                            <FormControlLabel
                                key={type}
                                value={type}
                                control={<Radio color="primary" />}
                                label={<RadioLabel {...UPDATE_TYPE_TITLES[type]} />}
                                css={css`
                                    margin-bottom: 12px;
                                `}
                            />
                        ))}
                    </RadioGroup>
                </Grid>
            );
            break;
        }
        case STEPS.DELETE: {
            content = (
                <Grid direction="row" container>
                    <RadioGroup
                        value={updateType || ''}
                        onChange={(e) => {
                            setUpdateType(e.target.value);
                        }}
                    >
                        {deleteTypes.map((type) => (
                            <FormControlLabel
                                key={type}
                                value={type}
                                control={<Radio color="primary" />}
                                label={<RadioLabel {...DELETE_TYPE_TITLES[type]} />}
                                css={css`
                                    margin-bottom: 12px;
                                `}
                            />
                        ))}
                    </RadioGroup>
                </Grid>
            );
            break;
        }
    }

    return (
        <ResponsiveSidebarDialog open={!!day} onClose={closeModal} maxWidth="md" fullWidth>
            <ModalTitle onClose={closeModal}>
                <Grid
                    container
                    direction="row"
                    css={css`
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: nowrap;
                    `}
                >
                    {/* The scheduling gate used to live here. It is a per-day decision and this
                        dialog is no longer opened from a day, so it belongs to the side dialog —
                        which can also do the whole day at once. See schedulingLocks.js. */}
                    <ModalHeader>{modalTitle}</ModalHeader>
                </Grid>
            </ModalTitle>
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                {content}
            </ModalContent>
            <StickyModalActions>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                    `}
                >
                    {/* Delete sits bottom-left, per the Figma. */}
                    {step === STEPS.RESOURCE && !!selectedResource?.resource_id ? (
                        <WarningButton
                            disabled={isLoading}
                            onClick={() => {
                                // Always the Delete step, which says what ends — a one-off
                                // schedule's dates included.
                                setUpdateType(deleteTypes[0]);
                                setStep(STEPS.DELETE);
                            }}
                            css={css`
                                max-width: 140px;
                                ${buttonLabel}
                            `}
                        >
                            Delete
                        </WarningButton>
                    ) : null}
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                    `}
                >
                    <Grid
                        container
                        direction="row"
                        css={css`
                            flex-wrap: nowrap;
                            justify-content: flex-end;
                        `}
                    >
                        <Grid
                            item
                            direction="column"
                            css={css`
                                margin-right: 12px;
                            `}
                        >
                            <SecondaryButton
                                onClick={closeModal}
                                css={css`
                                    max-width: 140px;
                                    ${buttonLabel}
                                `}
                            >
                                Cancel
                            </SecondaryButton>
                        </Grid>
                        <Grid item direction="column">
                            <PrimaryButton
                                disabled={!isValid || isLoading}
                                onClick={() => {
                                    if (step === STEPS.DELETE && updateType === UPDATE_TYPES.DELETE_RESOURCE) {
                                        callbacks.deleteResource({ resource: selectedResource }).then(closeModal);
                                        return;
                                    }

                                    // Asked only when rules already scheduled would be retired, or
                                    // the start date moved into the past.
                                    if (
                                        step === STEPS.RESOURCE &&
                                        !isNewResource &&
                                        needsScope({ resource: selectedResource, schedule: current, saved, today })
                                    ) {
                                        setUpdateType(scopeTypes[0]);
                                        setStep(STEPS.UPDATE);
                                        return;
                                    }

                                    callbacks.saveResource(payload).then((result) => {
                                        // Kept open on the name field, which is what has to change.
                                        if (step !== STEPS.DELETE && isDuplicateNameError(result)) {
                                            setRefusedNames((prev) => [...prev, updated.name]);
                                            setStep(STEPS.RESOURCE);
                                            return;
                                        }

                                        closeModal();
                                    });
                                }}
                                css={css`
                                    max-width: 140px;
                                    ${buttonLabel}
                                `}
                            >
                                Save
                            </PrimaryButton>
                        </Grid>
                    </Grid>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default EditResourceModal;
